import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import { DrawerForm } from '../../components/ui/drawer-form';
import OpenLiveAccountForm from '../../components/accounts/OpenLiveAccountForm';
import { getStatusColor, capitalizeFirst } from '../../utils';
import { LIVE_ACCOUNT_FILTER_FIELDS } from '../../constants/filters';
import {
  PlusCircle,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Shield,
  Download,
} from 'lucide-react';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import { LiveAccountData } from '../../components/accounts/OpenLiveAccountForm';
import { MT5AccountUser } from '../../utils/types';
import { mt5API } from '../../services/api';
import EditLeverageModal from '../../components/accounts/EditLeverageModal';

const LiveAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [showOpenAccountDrawer, setShowOpenAccountDrawer] = useState(false);
  const [isOpeningAccount, setIsOpeningAccount] = useState(false);
  const [selectedAccountForLeverage, setSelectedAccountForLeverage] = useState<{
    login: string | number;
    currentLeverage?: string | number;
    name?: string;
  } | null>(null);
  const [showEditLeverageModal, setShowEditLeverageModal] = useState(false);

  const filterFields = LIVE_ACCOUNT_FILTER_FIELDS;
  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await mt5API.getUsers();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching live accounts from MT5 API:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    console.log('Export live accounts data');
  };

  const columns: DataTableColumn<MT5AccountUser>[] = [
    {
      id: 'Login',
      header: 'Login',
      accessor: row => {
        const val = row.Login ?? (row as any).mt5Login ?? '-';
        return <span className="font-mono text-sm font-semibold">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Login ?? (row as any).mt5Login ?? 0,
    },
    {
      id: 'Name',
      header: 'Name',
      accessor: row => {
        const val = row.Name || (row as any).name || '-';
        return <span className="text-sm font-medium">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Name || (row as any).name || '',
    },
    {
      id: 'Group',
      header: 'Group',
      accessor: row => {
        const val = row.Group || (row as any).group || '-';
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            {val}
          </Badge>
        );
      },
      sortable: true,
      sortValue: row => row.Group || (row as any).group || '',
    },
    {
      id: 'Balance',
      header: 'Balance',
      accessor: row => {
        const val = Number(row.Balance ?? (row as any).balance ?? 0);
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      },
      sortable: true,
      sortValue: row => Number(row.Balance ?? (row as any).balance ?? 0),
    },
    {
      id: 'Credit',
      header: 'Credit',
      accessor: row => {
        const val = Number(row.Credit ?? (row as any).credit ?? 0);
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      },
      sortable: true,
      sortValue: row => Number(row.Credit ?? (row as any).credit ?? 0),
    },
    {
      id: 'Leverage',
      header: 'Leverage',
      accessor: row => {
        const val = row.Leverage ?? (row as any).leverage;
        return val !== undefined && val !== null && String(val).trim() !== ''
          ? String(val)
          : '-';
      },
      sortable: true,
      sortValue: row => Number(row.Leverage ?? (row as any).leverage ?? 0),
    },
    {
      id: 'Email',
      header: 'Email',
      accessor: row => {
        const val = row.Email || (row as any).email || '-';
        return <span className="text-sm text-gray-500">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Email || (row as any).email || '',
    },
    {
      id: 'Country',
      header: 'Country',
      accessor: row => {
        const val = row.Country || (row as any).country || '-';
        return <span className="text-sm">{val}</span>;
      },
      sortable: true,
      sortValue: row => row.Country || (row as any).country || '',
    },
    {
      id: 'Status',
      header: 'Status',
      accessor: row => {
        const val = row.Status || (row as any).status || 'Active';
        return (
          <Badge className={getStatusColor(val)}>{capitalizeFirst(val)}</Badge>
        );
      },
      sortable: true,
      sortValue: row => row.Status || (row as any).status || '',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => {
        const loginVal = String(
          row.Login ?? (row as any).mt5Login ?? (row as any).mt5Login ?? ''
        );
        return (
          <ActionDropdown
            id={loginVal}
            showView={false}
            showDelete={false}
            showDeposit={true}
            showEdit={false}
            showWithdraw={true}
            onDeposit={() => handleDeposit(loginVal)}
            onWithdraw={() => handleWithdraw(loginVal)}
          />
        );
      },
    },
  ];

  const navigate = useNavigate();

  const handleDeposit = (accountId: string) => {
    if (!accountId) return;
    navigate(`/payments/deposit?login=${accountId}`, {
      state: { login: accountId },
    });
  };

  const handleWithdraw = (accountId: string) => {
    if (!accountId) return;
    navigate(`/payments/withdrawal?login=${accountId}`, {
      state: { login: accountId },
    });
  };

  const handleCreateLiveAccount = () => {
    setShowOpenAccountDrawer(true);
  };

  const handleLiveAccountSubmit = async (data: LiveAccountData) => {
    setIsOpeningAccount(true);
    try {
      const formatPassword = (pass?: string) => {
        if (!pass || !pass.trim()) return undefined;
        const p = pass.trim();
        return p.endsWith('!') ? p : p + '!';
      };

      const payload = {
        group: data.group,
        name: data.name,
        mainPassword: formatPassword(data.mainPassword),
        investPassword: formatPassword(data.investPassword),
        leverage: data.leverage,
      };
      await mt5API.createUser(payload);
      setShowOpenAccountDrawer(false);
      toast.success('Live account created successfully!');
      fetchAccounts();
    } catch (error: any) {
      console.error('Error creating live account:', error);
      toast.error(
        error?.response?.data?.message ||
          'Error creating live account. Please try again.'
      );
    } finally {
      setIsOpeningAccount(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const filteredAccounts = accounts.filter(account => {
    const loginStr = String(account.Login || (account as any).mt5Login || '');
    const nameStr = (account.Name || (account as any).name || '').toLowerCase();
    const groupStr = (
      account.Group ||
      (account as any).group ||
      ''
    ).toLowerCase();
    const emailStr = (
      account.Email ||
      (account as any).email ||
      ''
    ).toLowerCase();
    const countryStr = (
      account.Country ||
      (account as any).country ||
      ''
    ).toLowerCase();
    const statusStr = (
      account.Status ||
      (account as any).status ||
      'active'
    ).toLowerCase();
    const levStr = String(account.Leverage || (account as any).leverage || '');
    const balStr = String(account.Balance ?? (account as any).balance ?? 0);
    const credStr = String(account.Credit ?? (account as any).credit ?? 0);
    const sTerm = searchTerm.toLowerCase();

    const matchesSearch =
      searchTerm === '' ||
      loginStr.includes(sTerm) ||
      nameStr.includes(sTerm) ||
      groupStr.includes(sTerm) ||
      emailStr.includes(sTerm) ||
      countryStr.includes(sTerm) ||
      statusStr.includes(sTerm) ||
      levStr.includes(sTerm) ||
      balStr.includes(sTerm) ||
      credStr.includes(sTerm);

    const matchesStatus =
      !activeFilters.status || statusStr === activeFilters.status.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const totalBalance = accounts.reduce(
    (acc, curr) => acc + (curr.Balance || 0),
    0
  );
  const totalCredit = accounts.reduce(
    (acc, curr) => acc + (curr.Credit || 0),
    0
  );

  return (
    <div className="relative">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Live Accounts"
          description="Get help with your account, trading, and platform issues"
          actionLabel="Open Live Account"
          actionIcon={PlusCircle}
          onAction={handleCreateLiveAccount}
        />
      </div>

      <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Balance"
          value={`$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Across all live accounts"
          icon={DollarSign}
        />

        <StatsCard
          title="Total Credit"
          value={`$${totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Total credit bonus"
          icon={TrendingUp}
        />

        <StatsCard
          title="Total Accounts"
          value={accounts.length.toString()}
          change="Live account count"
          icon={AlertTriangle}
        />

        <StatsCard
          title="Active Accounts"
          value={accounts
            .filter(a => (a.Status || 'active').toLowerCase() === 'active')
            .length.toString()}
          change="Currently active"
          icon={Shield}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Live Accounts</CardTitle>
            </div>
            <div className="flex space-x-2">
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button> */}
              <Button variant="outline" size="sm" onClick={handleExportData}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Live Accounts"
          />
          {loading ? (
            <LoadingSkeleton type="table" rows={5} cols={9} />
          ) : (
            <DataTable
              columns={columns}
              data={filteredAccounts}
              searchValue={searchTerm}
              searchPlaceholder="Search by Login, Name, Group, Email..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showColumnVisibility={true}
              showAddButton={false}
              showExportButton={false}
              initialPageSize={10}
              initialSortBy={{ id: 'Login', desc: true }}
            />
          )}
        </CardContent>
      </Card>

      <DrawerForm<LiveAccountData>
        open={showOpenAccountDrawer}
        onOpenChange={setShowOpenAccountDrawer}
        title="Open Live Account"
        description="Apply for a live trading account with real funds"
        onSubmit={handleLiveAccountSubmit}
        submitText="Submit Application"
        loading={isOpeningAccount}
        width="xl"
      >
        <OpenLiveAccountForm onSubmit={handleLiveAccountSubmit} />
      </DrawerForm>

      <EditLeverageModal
        isOpen={showEditLeverageModal}
        onClose={() => {
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        onSuccess={() => {
          fetchAccounts();
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        account={selectedAccountForLeverage}
      />
    </div>
  );
};

export default LiveAccounts;
